import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import useApi from '../useApi';

const REFRESH_STATUS_HEADERS = {
  'Cache-Control': 'no-cache',
  Pragma: 'no-cache',
  Expires: '0',
};

const fetchRefreshStatusSnapshot = async () => {
  const { data } = await axios.get('/api/cache/refresh-status', {
    headers: REFRESH_STATUS_HEADERS,
  });
  return data;
};

const useRefreshSync = ({ setSnackbar }) => {
  const { useForceSync, useAbortSync } = useApi();
  const { mutate: startRefresh, isSuccess, isError, error, isPending: isSyncRequestPending } = useForceSync();
  const { mutate: abortRefresh, isPending: isAbortPending } = useAbortSync();
  const [isSyncing, setIsSyncing] = useState(false);
  const [programSyncing, setProgramSyncing] = useState(false);

  const formatAbortSummary = useCallback((payload) => {
    const summaries = payload?.summaries || {};
    const completed = [];
    const remaining = [];
    Object.entries(summaries).forEach(([programName, meta]) => {
      const status = (meta?.status || '').toLowerCase();
      if (status === 'success' || status === 'complete') {
        completed.push(programName);
      } else {
        remaining.push(programName);
      }
    });
    const completedText = completed.length ? `Completed: ${completed.join(', ')}` : 'Completed: none';
    const pendingText = remaining.length ? `Pending: ${remaining.join(', ')}` : 'Pending: none';
    return `${payload?.reason || 'Refresh aborted.'} ${completedText}. ${pendingText}.`;
  }, []);

  useEffect(() => {
    if (isSuccess) {
      setSnackbar({ open: true, message: 'Cache sync complete!', severity: 'success' });
    }
    if (isError) {
      setSnackbar({ open: true, message: `Sync failed: ${error.message}`, severity: 'error' });
    }
  }, [isSuccess, isError, error, setSnackbar]);

  useEffect(() => {
    const loadInitialStatus = async () => {
      try {
        const data = await fetchRefreshStatusSnapshot();
        setIsSyncing(data?.status === 'in_progress');
      } catch (statusError) {
        console.warn('Failed to load refresh status on mount', statusError);
      }
    };
    loadInitialStatus();
  }, []);

  useEffect(() => {
    if (!isSyncing) return undefined;
    const timeoutId = setTimeout(() => {
      console.warn('[Sync] forcing spinner off after timeout safeguard');
      setIsSyncing(false);
      setProgramSyncing(false);
    }, 4 * 60 * 1000);
    return () => clearTimeout(timeoutId);
  }, [isSyncing]);

  const handleForceSync = useCallback(() => {
    if (isSyncing) {
      setSnackbar({ open: true, message: 'A sync is already running.', severity: 'info' });
      return;
    }
    setSnackbar({ open: true, message: 'Sync process started!', severity: 'info' });
    setIsSyncing(true);
    startRefresh(undefined, {
      onError: () => setIsSyncing(false),
    });
  }, [isSyncing, setSnackbar, startRefresh]);

  const handleAbortSync = useCallback(() => {
    abortRefresh(undefined, {
      onSuccess: (data) => {
        setIsSyncing(false);
        const summaries = Object.entries(data?.programs || {}).reduce((acc, [programName, status]) => {
          acc[programName] = { status };
          return acc;
        }, {});
        setSnackbar({
          open: true,
          message: formatAbortSummary({ reason: data?.reason, summaries }),
          severity: 'warning',
        });
      },
      onError: (abortError) => {
        setSnackbar({
          open: true,
          message: abortError?.response?.data?.message || abortError?.message || 'Failed to abort sync.',
          severity: 'error',
        });
      },
    });
  }, [abortRefresh, formatAbortSummary, setSnackbar]);

  const syncSpinning = isSyncing || isSyncRequestPending || programSyncing;

  return {
    isSyncing,
    setIsSyncing,
    programSyncing,
    setProgramSyncing,
    syncSpinning,
    handleForceSync,
    handleAbortSync,
    formatAbortSummary,
    isSyncRequestPending,
    isAbortPending,
  };
};

export default useRefreshSync;
